import { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import { TextField } from '@mui/material';

export type EditableFieldProps = {
  value?: string;
  onSave?: (newValue: string) => void;
  showEditButton?: boolean;
  placeholder?: string;
  linkHref?: string;
  className?: string;
};

export default function EditableField({
  value = '',
  onSave,
  showEditButton = false,
  placeholder = 'Enter value',
  linkHref,
  className,
}: EditableFieldProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value);

  const handleEditClick = (): void => {
    setEditedValue(value);
    setIsEditing(true);
  };

  const handleSaveClick = (): void => {
    if (onSave && editedValue !== value) {
      onSave(editedValue);
    }
    setIsEditing(false);
  };

  const handleCancelClick = (): void => {
    setEditedValue(value);
    setIsEditing(false);
  };

  const handleKeyPress = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter') {
      handleSaveClick();
    } else if (event.key === 'Escape') {
      handleCancelClick();
    }
  };

  if (isEditing) {
    return (
      <div
        className={className}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}
      >
        <TextField
          value={editedValue}
          onChange={(e) => setEditedValue(e.target.value)}
          onKeyDown={handleKeyPress}
          size="small"
          variant="outlined"
          placeholder={placeholder}
          autoFocus
        />
        <button
          type="button"
          onClick={handleSaveClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            color: '#666',
          }}
          title="Save"
        >
          <CheckIcon fontSize="small" />
        </button>
        <button
          type="button"
          onClick={handleCancelClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            color: '#666',
          }}
          title="Cancel"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>
    );
  }

  return (
    <div className={className} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {linkHref ? (
        <a
          target="_blank"
          href={linkHref}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: '#007bff',
            cursor: 'pointer',
            fontSize: '14px',
            lineHeight: '150%',
            textDecoration: 'none',
          }}
          rel="noreferrer"
        >
          <LinkIcon style={{ fontSize: '14px' }} />
          {value}
        </a>
      ) : (
        <span style={{ fontSize: '14px', lineHeight: '150%' }}>{value}</span>
      )}

      {showEditButton && (
        <button
          type="button"
          onClick={handleEditClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            color: '#666',
          }}
          title="Edit"
        >
          <EditIcon fontSize="small" />
        </button>
      )}
    </div>
  );
}
